/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Grid, Link, Typography2 as Typography, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type { PremiumProductFields_Dedupe1Fragment as Degree } from 'bundles/premium-hub/queries/__generated__/PremiumProductFieldsFragment';

import _t from 'i18n!nls/premium-hub';

type Props = {
  degree?: Degree | null;
};

const styles = {
  container: css`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    padding: var(--cds-spacing-150);

    &:has(a:focus-visible) {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
      border-radius: var(--cds-border-radius-100);
    }
  `,
  degreeContent: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
  `,
  image: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
  `,
  stretchedLink: css`
    &::before {
      content: '';
      cursor: inherit;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      width: 100%;
      height: 100%;
    }
  `,
  learnMoreText: css`
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
};

const RelatedDegree = ({ degree }: Props) => {
  if (!degree) return null;

  return (
    <Grid container spacing={16}>
      <Grid item xs={12}>
        <Typography variant="subtitleMedium" component="p">
          {_t('Start learning with this degree today')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <div css={styles.container}>
          <div css={styles.degreeContent}>
            <Imgix
              alt={degree?.partnerEntities[0]?.name ?? ''}
              src={degree.partnerEntities[0]?.squareLogo ?? ''}
              height={48}
              width={48}
              css={styles.image}
            />
            <Typography variant="bodyPrimary" component="p">
              {degree.name}
            </Typography>
          </div>
          <Link
            href={`/degrees/${degree.slug}`}
            variant="quiet"
            icon={<ArrowNextIcon size="large" />}
            iconPosition="after"
            aria-label={_t('Learn more about #{degreeName}', { degreeName: degree.name })}
            css={styles.stretchedLink}
          >
            <span css={styles.learnMoreText}>{_t('Learn more')}</span>
          </Link>
        </div>
      </Grid>
    </Grid>
  );
};

export default RelatedDegree;
